import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Mail, Phone, Check, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminContact() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact-messages", { params: { limit: 100 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) { /* swallow */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contact-messages/${id}`, { read: true });
      load();
    } catch (err) { /* swallow */ }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-contact-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">
              Contact inbox
              {unread > 0 && (
                <span className="ml-3 text-sm align-middle px-2 py-0.5 rounded-full bg-primary text-primary-foreground" data-testid="contact-unread-count">
                  {unread} new
                </span>
              )}
            </h1>
          </div>
          <Button variant="outline" onClick={load} className="border-border" data-testid="contact-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-16 text-center text-muted-foreground">
            <Mail className="w-8 h-8 mx-auto mb-3 opacity-60" />
            No messages yet.
          </div>
        ) : (
          <div className="border border-border rounded-xl bg-card divide-y divide-border" data-testid="contact-list">
            {items.map((m) => {
              const isOpen = openId === m.id;
              return (
                <div key={m.id} className={`p-4 ${!m.read ? "bg-primary/5" : ""}`} data-testid={`contact-row-${m.id}`}>
                  <button
                    type="button"
                    className="w-full text-left flex items-center gap-3"
                    onClick={() => setOpenId(isOpen ? null : m.id)}
                  >
                    <span className={`w-2 h-2 rounded-full ${m.read ? "bg-muted-foreground/40" : "bg-primary"}`} />
                    <span className="font-medium flex-1 truncate">{m.subject}</span>
                    <span className="text-xs text-muted-foreground hidden sm:inline">{m.name}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(m.created_at)}</span>
                    <span className="text-[10px] uppercase tracking-widest text-primary">{m.locale}</span>
                  </button>
                  {isOpen && (
                    <div className="mt-3 pl-5 text-sm space-y-3 text-muted-foreground" data-testid={`contact-detail-${m.id}`}>
                      <div className="flex flex-wrap gap-x-5 gap-y-1">
                        <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</span>
                        {m.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</span>}
                      </div>
                      <p className="whitespace-pre-wrap text-foreground">{m.message}</p>
                      <div>
                        {!m.read && (
                          <Button size="sm" variant="outline" className="border-border" onClick={() => markRead(m.id)} data-testid={`contact-mark-read-${m.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1.5" /> Mark as read
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
